'use client'

import { useGetFareCalendarQuery } from '@/src/api/rtkQuery/calendarApi'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { buildFareCalendarRequest } from '@/src/utils/srp/searchUtils/buildFareCalendarRequest'
import dayjs from 'dayjs'
import 'dayjs/locale/ar'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useParams, useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DateCarouselItem } from './DateCarousel'
import DateCarousel from './DateCarousel'
import { isFlyin } from '@/src/utils/generalUtils/isFlyin'

dayjs.extend(customParseFormat)
const BUFFER = 9
const today = dayjs()

export interface FareCalendarResponse {
    tripType: string
    dayData: {
        amount: number
        date: string
        fareCategory: string
        farePresent: boolean
    }[]
}

const DateCarouselWrapper = () => {
    const { urlQueryObj, isSeoQuery } = useGetSearchParamsObj()
    const { isPerPaxPricingEnabled, jsons } = useSearchResults()
    const partnerId = jsons?.userInfo?.partnerId || ''
    const routeParams = useParams()
    const locale = (routeParams?.locale as string) || 'en'

    // Set dayjs locale based on route locale
    useEffect(() => {
        dayjs.locale(locale)
    }, [locale])

    const selectedDate = urlQueryObj?.depart_date || ''

    const visibleCount = 7

    const { formatCurrency } = useFormatCurrencyForSrp()

    // Calculate initial bufferStartIdx based on selected date to avoid unnecessary API call with today's date
    const initialBufferStartIdx = useMemo(() => {
        if (!selectedDate) return 0
        const selected = dayjs(selectedDate, 'DD/MM/YYYY', 'true')
        if (!selected.isValid()) return 0
        const actualSelected = selected.isBefore(today.startOf('day')) ? today : selected
        const daysFromToday = actualSelected.diff(today.startOf('day'), 'day')
        return Math.floor(daysFromToday / BUFFER) * BUFFER
    }, [selectedDate])

    const [startIdx, setStartIdx] = useState(0)
    const [bufferStartIdx, setBufferStartIdx] = useState(initialBufferStartIdx)
    const [selectedIdx, setSelectedIdx] = useState(0)
    const [dayDataCache, setDayDataCache] = useState<Record<string, { amount: number; fareCategory: string }>>({})
    const prevSelectedDate = useRef<string | null>(null)

    const bufferStartDate = useMemo(() => {
        // Always start from today for the first buffer
        if (bufferStartIdx === 0) {
            return today.startOf('day')
        }
        // For subsequent buffers, continue from today + bufferStartIdx
        return today.startOf('day').add(bufferStartIdx, 'day')
    }, [bufferStartIdx])

    // Remove minStartIdx as we're always starting from today or later

    useEffect(() => {
        const selected = dayjs(selectedDate, 'DD/MM/YYYY', 'true')
        const validSelected = selected.isValid() ? selected : today
        const actualSelected = validSelected.isBefore(today.startOf('day')) ? today : validSelected

        // If selected date changed and requires a different buffer, update buffer first
        if (prevSelectedDate.current !== null && prevSelectedDate.current !== selectedDate) {
            const daysFromToday = actualSelected.diff(today.startOf('day'), 'day')
            const newBufferStartIdx = Math.floor(daysFromToday / BUFFER) * BUFFER
            if (newBufferStartIdx !== bufferStartIdx) {
                setBufferStartIdx(newBufferStartIdx)
                // Return early - this will trigger effect again with new bufferStartDate
                prevSelectedDate.current = selectedDate
                return
            }
        }

        // Calculate indices based on current buffer
        const idx = actualSelected.diff(bufferStartDate, 'day')
        setSelectedIdx(idx)

        // On initial load, also set startIdx to center the selected date
        if (prevSelectedDate.current === null) {
            setStartIdx(Math.max(0, idx - Math.floor(visibleCount / 2)))
        }

        prevSelectedDate.current = selectedDate
    }, [selectedDate, visibleCount, bufferStartDate, bufferStartIdx])

    const payload = buildFareCalendarRequest({
        startDate: bufferStartDate.format('DD/MM/YYYY'),
        daysCount: BUFFER,
        searchParams: urlQueryObj,
        partnerId: isFlyin() ? partnerId : undefined
    })

    const { data } = useGetFareCalendarQuery({ payload, perPaxPricingRequired: isPerPaxPricingEnabled })

    useEffect(() => {
        if (data?.fareCalendar) {
            // RTK query transform returns a YYYY-MM-DD lookup already; just merge into cache
            setDayDataCache(prev => ({ ...prev, ...data.fareCalendar }))
        }
    }, [data])

    const items: DateCarouselItem[] = useMemo(() => {
        return Array.from({ length: visibleCount }, (_, i) => {
            const idx = startIdx + i
            const date = bufferStartDate.add(idx, 'day')
            const dateKey = date.format('YYYY-MM-DD')
            const fare = dayDataCache[dateKey]
            const price = fare?.amount
            // Clone date and set locale for this specific format
            const localizedDate = date.locale(locale)
            return {
                date: localizedDate.format('D MMM'),
                day: localizedDate.format('ddd'),
                price: price !== undefined && price !== null && price > 0 ? formatCurrency(price) : '-',
                isSelected: idx === selectedIdx,
                isLowest: fare?.fareCategory === 'CHEAPEST'
            }
        })
    }, [startIdx, selectedIdx, dayDataCache, bufferStartDate, formatCurrency, visibleCount, locale])

    const handlePrev = useCallback(() => {
        if (startIdx > 0) {
            setStartIdx(startIdx - 1)
        } else if (bufferStartIdx > 0) {
            // Load previous buffer
            setBufferStartIdx(bufferStartIdx - BUFFER)
            setStartIdx(BUFFER - visibleCount)
        }
        // If we're at the beginning (bufferStartIdx === 0 and startIdx === 0), do nothing
    }, [startIdx, bufferStartIdx, visibleCount])

    const handleNext = useCallback(() => {
        if (startIdx + visibleCount < BUFFER) {
            setStartIdx(prev => prev + 1)
        } else {
            setBufferStartIdx(prev => prev + BUFFER)
            setStartIdx(0)
        }
    }, [startIdx, visibleCount])

    const router = useRouter()
    const handleSelect = useCallback(
        (index: number) => {
            const actualIdx = startIdx + index
            setSelectedIdx(actualIdx)
            const selectedDay = bufferStartDate.add(actualIdx, 'day').format('DD/MM/YYYY')
            const params = new URLSearchParams(urlQueryObj)
            params.set('depart_date', selectedDay)
            const newUrl = isSeoQuery
                ? `/${locale}/flights/results?${params.toString()}`
                : `${window.location.pathname}?${params.toString()}`
            router.push(newUrl)
        },
        [startIdx, bufferStartDate, router]
    )

    // Check if we can navigate previous (only if not at the very beginning)
    const canPrev = startIdx > 0 || bufferStartIdx > 0
    const canNext = true

    return (
        <DateCarousel
            items={items}
            onPrev={handlePrev}
            onNext={handleNext}
            onSelect={handleSelect}
            canPrev={canPrev}
            canNext={canNext}
            isRTL={locale === 'ar'}
        />
    )
}

export { DateCarouselWrapper }
export default DateCarouselWrapper
